import { z } from "zod";
import { AGENT_PROTOCOL_VERSION, appIdSchema, commandIdSchema, correlationIdSchema, isoTimestampSchema, runIdSchema, threadIdSchema, userIdSchema, } from "./identifiers.js";
import { agentAppEventNoticeSchema } from "./app-events.js";
import { agentActorSchema, agentTranscriptSnapshotSchema } from "./messages.js";
import { agentSkillCatalogSnapshotSchema } from "./skills.js";
import { agentAttachmentsSchema, hasMessageContent } from "./attachments.js";
import { continuityCapabilitySchema } from "./continuity.js";
import { taskPreparationRequestSchema } from "./task-preparation.js";
export const agentRunStatusSchema = z.enum([
    "queued",
    "provisioning",
    "running",
    "idle",
    "waiting_for_user",
    "pausing",
    "paused",
    "resuming",
    "cancelling",
    "failed",
    "completed",
    "cancelled",
]);
export const agentRunPhaseSchema = z.enum([
    "starting",
    "understanding",
    "building",
    "testing",
    "verifying",
    "deploying",
    "diagnosing",
    "repairing",
    "waiting",
    "finished",
]);
export const agentRunTriggerSchema = z.enum([
    "user_message",
    "incident",
    "retry",
    "system",
]);
export const agentRunSchema = z
    .object({
    schemaVersion: z.literal(1),
    id: runIdSchema,
    userId: userIdSchema,
    appId: appIdSchema,
    threadId: threadIdSchema,
    parentRunId: runIdSchema.nullable(),
    correlationId: correlationIdSchema,
    trigger: agentRunTriggerSchema,
    status: agentRunStatusSchema,
    phase: agentRunPhaseSchema,
    checkpointVersion: z.number().int().nonnegative(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
})
    .strict();
export const agentRunStatusCauseSchema = z.enum([
    "request",
    "scheduler",
    "runtime_event",
    "user_command",
    "recovery",
]);
/**
 * The bounded lifecycle projection consumed by conversation clients. Raw
 * runtime events, prompts, model output, and error messages deliberately do
 * not cross this contract.
 */
export const agentRunStatusEventSchema = z
    .object({
    schemaVersion: z.literal(1),
    type: z.literal("agent.run.status"),
    userId: userIdSchema,
    appId: appIdSchema,
    threadId: threadIdSchema,
    runId: runIdSchema,
    parentRunId: runIdSchema.nullable(),
    correlationId: correlationIdSchema,
    sequence: z.number().int().nonnegative(),
    previousStatus: agentRunStatusSchema.nullable(),
    status: agentRunStatusSchema,
    phase: agentRunPhaseSchema,
    checkpointVersion: z.number().int().nonnegative(),
    cause: agentRunStatusCauseSchema,
    occurredAt: isoTimestampSchema,
})
    .strict();
const commandBase = {
    continuity: continuityCapabilitySchema.optional(),
    // New roots opt in; a persisted historical root never acquires new tools implicitly.
    fileAccessVersion: z.literal(1).optional(),
    protocolVersion: z.literal(AGENT_PROTOCOL_VERSION),
    commandId: commandIdSchema,
    runId: runIdSchema,
    sequence: z.number().int().nonnegative(),
    issuedAt: isoTimestampSchema,
    // Person requesting this action; a retry can preserve another author's input.
    actor: agentActorSchema.optional(),
};
const userInputSchema = z
    .object({
    messageId: z.uuid(),
    content: z.string().max(200_000),
    attachments: agentAttachmentsSchema.optional(),
    actor: agentActorSchema.optional(),
})
    .strict().refine(hasMessageContent, "An input needs text or an attachment");
export const agentCommandSchema = z.discriminatedUnion("type", [
    z
        .object({
        ...commandBase,
        type: z.literal("run.start"),
        input: userInputSchema,
        assignment: z.object({
            taskRunId: runIdSchema,
            timeoutSeconds: z.number().int().min(60).max(3_600),
            preparation: taskPreparationRequestSchema.optional(),
            captures: z.array(z.object({ assignmentId: z.string().min(1).max(128),
                url: z.url().max(2_048), submissionKey: z.string().regex(/^[a-f0-9]{64}$/),
            }).strict()).max(50).optional(),
        }).strict().optional(),
        // Optional only for protocol-v1 compatibility with already persisted
        // commands. Current root-run creation always persists this snapshot.
        transcript: agentTranscriptSnapshotSchema.optional(),
        // AI-SDK Codex roots snapshot the enabled catalog. Optionality preserves
        // historical commands and harnesses that do not consume these skills.
        skillCatalog: agentSkillCatalogSnapshotSchema.optional(),
        // Continuity roots: app changes since the previous turn, frozen at claim.
        appEventNotice: agentAppEventNoticeSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...commandBase,
        type: z.literal("run.continue"),
        checkpointVersion: z.number().int().nonnegative(),
        input: userInputSchema.optional(),
        // Present when this is sequence zero for a new checkpoint-backed root
        // run. Same-root continuations rely on native session state instead.
        transcript: agentTranscriptSnapshotSchema.optional(),
        // Present on sequence zero when an AI-SDK Codex checkpoint retry creates
        // a root. Same-root continuations retain their original root snapshot.
        skillCatalog: agentSkillCatalogSnapshotSchema.optional(),
        appEventNotice: agentAppEventNoticeSchema.optional(),
    })
        .strict(),
    z
        .object({
        ...commandBase,
        type: z.literal("user.answer"),
        questionId: z.uuid(),
        input: userInputSchema,
    })
        .strict(),
    z
        .object({
        ...commandBase,
        type: z.literal("run.pause"),
        reason: z.string().trim().min(1).max(500),
    })
        .strict(),
    z
        .object({
        ...commandBase,
        type: z.literal("run.cancel"),
        reason: z.string().trim().min(1).max(500).optional(),
    })
        .strict(),
]).superRefine((command, context) => {
    if ((command.type === "run.start" || command.type === "run.continue") &&
        command.skillCatalog &&
        command.sequence !== 0) {
        context.addIssue({
            code: "custom",
            path: ["skillCatalog"],
            message: "Only a root command may carry a skill catalog snapshot",
        });
    }
    if ((command.type === "run.start" || command.type === "run.continue") &&
        command.appEventNotice &&
        (command.sequence !== 0 || !command.continuity)) {
        context.addIssue({
            code: "custom",
            path: ["appEventNotice"],
            message: "Only a continuity root command may carry an app event notice",
        });
    }
    if ((command.type !== "run.start" && command.type !== "run.continue") ||
        !command.transcript) {
        return;
    }
    const triggeringItem = command.transcript.items.at(-1);
    if (!command.input ||
        command.transcript.throughMessageId !== command.input.messageId ||
        triggeringItem?.id !== command.input.messageId ||
        triggeringItem.content !== command.input.content ||
        JSON.stringify(triggeringItem.attachments ?? []) !== JSON.stringify(command.input.attachments ?? []) ||
        (triggeringItem.actor && command.input.actor &&
            triggeringItem.actor.userId !== command.input.actor.userId)) {
        context.addIssue({
            code: "custom",
            path: ["transcript"],
            message: "Transcript trigger must match the command input",
        });
    }
});
//# sourceMappingURL=runs.js.map